<script lang="ts" setup>
import type { FocusEvent } from '@/objects/generated';
import { onActivated, onMounted, ref } from 'vue';
import { EventDao } from '@/dao';
import { useToastStore } from '@/stores/toastStore';
import { useIsViewActive, useTaskLoader } from '@/composeables/loaders';
import SignupFocusEventList from '@/components/focuseventcard/SignupFocusEventList.vue';
import LastCreatedEventList from '@/components/focuseventcard/LastCreatedEventList.vue';
import signalrSubscribe from '@/objects/generated/signalr/signalrSubscribe';
import { LoadingComp } from '@/components';

const viewIsCurrentlyActive = useIsViewActive();

const latestevents = ref<Array<FocusEvent>>([]);
const signupstartingsoon = ref<Array<FocusEvent>>([]); // TODO: This should be loaded from usersetting

const { AddLoader: latestEventsAddLoader, LoadState: latestEventsLoaderState } = useTaskLoader();
const { AddLoader: signupStartingSoonLoader, LoadState: signupStartingSoonEventsLoadState } = useTaskLoader();

const loadLatestEvents = async () => {
    try {
        latestevents.value = await EventDao.Focus('latest', 10);
    } catch (error: any) {
        useToastStore().add({ level: 'ERROR', message: error.message });
        throw error;
    }
};

const loadSignupStartingSoon = async () => {
    try {
        signupstartingsoon.value = await EventDao.Focus('signupsoon', 10);
    } catch (error: any) {
        useToastStore().add({ level: 'ERROR', message: error.message });
        throw error;
    }
};

latestEventsAddLoader(
    loadLatestEvents,
    'focus.loading_latestevents',
    'fa-flag-checkered', // pick one
);

signupStartingSoonLoader(
    loadSignupStartingSoon,
    'focus.loading_eventswheresignupstartssoon',
    'fa-flag-checkered', // pick one
);

// Note: According to the documentation, onActivated should trigger on mount, but it does not in this case and I do not know why
onActivated(() => {
    subscribeToSignalrEventChangesIfUnsubscribed();
    reloadIfNeeded();
});

onMounted(() => subscribeToSignalrEventChangesIfUnsubscribed());

const needsReloading = {
    latest: false,
    signupsoon: false,
};

const reloadIfNeeded = async () => {
    if (needsReloading.latest) {
        await loadLatestEvents();
        needsReloading.latest = false;
    }

    if (needsReloading.signupsoon) {
        await loadSignupStartingSoon();
        needsReloading.signupsoon = false;
    }
};

let signalrUnsubscribeFunction: (() => Promise<void>) | null = null;
let signalrSubscribePromise: Promise<() => Promise<void>> | null = null;

const subscribeToSignalrEventChangesIfUnsubscribed = async () => {
    if (signalrSubscribePromise !== null) {
        return;
    }
    signalrSubscribePromise = signalrSubscribe.DbDataChangedNotification({ DtoType: 'FocusEvent' }, async (notifications) => {
        if (notifications.some((n) => n.Operation == 'Insert')) {
            needsReloading.latest = true;
            needsReloading.signupsoon = true;
        }

        const ids = notifications.filter((n) => ['Update', 'Delete'].includes(n.Operation)).map((n) => n.ID);

        if (latestevents.value.some((e) => ids.includes(e.ID))) {
            needsReloading.latest = true;
        }

        if (signupstartingsoon.value.some((e) => ids.includes(e.ID))) {
            needsReloading.signupsoon = true;
        }

        if (viewIsCurrentlyActive.value) {
            reloadIfNeeded();
        } else {
            // If the view is not active and we have gotten a message that we need to reload then we can unsubscribe from signalr
            if (needsReloading.latest && needsReloading.signupsoon && signalrUnsubscribeFunction) {
                signalrUnsubscribeFunction();
            }
        }
    });
    signalrUnsubscribeFunction = await signalrSubscribePromise;
    signalrSubscribePromise = null;
};
</script>

<template>
    <div class="d-flex flex-column w-100">
        <LoadingComp
            :label="$tm('home.loadinglatestevents')"
            :state="latestEventsLoaderState"
            type="table"
        />
        <template v-if="latestevents.length != 0">
            <h5>{{ $tm('home.latestevents') }}</h5>
            <LastCreatedEventList :events="latestevents" />
        </template>

        <hr class="my-5" />
        <LoadingComp
            :label="$tm('home.loadingsignupstartingsoonevents')"
            :state="signupStartingSoonEventsLoadState"
            type="table"
        />
        <template v-if="signupstartingsoon.length != 0">
            <h5>{{ $tm('home.signupstartingsoon') }}</h5>
            <SignupFocusEventList :events="signupstartingsoon" />
        </template>

        <hr class="my-5" />
    </div>
</template>
